import '../app/global-styles';

import { ApplicationRef, ComponentRef, createComponent } from '@angular/core';
import { createApplication } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { API_NAMES } from '../api/api-names';
import { AppRoot } from '../app/app-root/app-root';
import { appConfig } from '../app/app.config';
import {
  type AngularRuntimeConfig,
  normalizeOriginRelativeBaseUrl,
  parseApiBaseUrls,
  RUNTIME_CONFIG,
} from '../shell/runtime/config';
import { MFE_HOST_ELEMENT } from '../shell/runtime/failure-reporter';
import { TranslationService } from '../shell/translation-service';

/*
 * Runtime configuration is read once, at connect time. Changing any of these
 * after bootstrap is rejected — the MFE must be remounted instead.
 */
const IMMUTABLE_CONFIG_ATTRIBUTES = new Set(['api-base-url', 'api-base-urls', 'i18n-base-url']);

// eslint-disable-next-line functional/no-class-inheritance
class AngularProfileWidgetElement extends HTMLElement {
  // FIXED: Definiert für den Browser, auf welche Attribute er lauschen muss (W3C Standard)
  // The Shell (platform-shell) always sets the single canonical "active-user"
  // attribute (see MfeRouteOutlet.tsx), so no other spelling needs to be observed.
  // "route" is included so the Shell can drive this MFE's internal Angular
  // Router when composing / and /store on its own routes.
  // "locale" / "catalogue-revision" drive i18n catalogue loading; "api-base-url"
  // / "i18n-base-url" / "api-base-urls" configure RUNTIME_CONFIG once, at
  // connect time.
  static readonly observedAttributes = [
    'active-user',
    'route',
    'locale',
    'catalogue-revision',
    'api-base-url',
    'i18n-base-url',
    'api-base-urls',
  ];

  private appRef: ApplicationRef | null = null;
  private componentRef: ComponentRef<AppRoot> | null = null;

  async attributeChangedCallback(
    name: string,
    _old: null | string,
    value: null | string,
  ): Promise<void> {
    if (!this.componentRef) {
      return;
    }

    if (name === 'route') {
      const router = this.componentRef.injector.get(Router);

      try {
        await navigateToRoute(router, value);
      } catch (error: unknown) {
        console.error('[AngularProfileWidgetElement] Navigation failed.', error);
      }

      return;
    }

    if (name === 'active-user') {
      this.componentRef.setInput('activeUser', value ?? 'Anonymous User');
      this.componentRef.changeDetectorRef.detectChanges();

      return;
    }

    if (IMMUTABLE_CONFIG_ATTRIBUTES.has(name)) {
      console.error(
        'Angular Profile MFE runtime configuration cannot change after bootstrap. Remount the MFE with a new configuration instead.',
      );

      return;
    }

    if (name !== 'locale' && name !== 'catalogue-revision') {
      return;
    }

    const translationService = this.componentRef.injector.get(TranslationService);

    try {
      await translationService.setContext(
        this.getAttribute('locale'),
        this.getAttribute('catalogue-revision'),
      );

      this.componentRef.changeDetectorRef.detectChanges();
    } catch (error: unknown) {
      console.error('[AngularProfileWidgetElement] Failed to refresh translations.', error);
    }
  }

  async connectedCallback(): Promise<void> {
    try {
      const runtimeConfig = readRuntimeConfig(this);

      // 1. Initialisiert die zoneless Angular-Anwendung headlessly
      this.appRef = await createApplication({
        ...appConfig,
        providers: [
          ...appConfig.providers,
          // Lets RuntimeFailureReporter dispatch the runtime-failure
          // CustomEvent from this exact custom-element instance, so the
          // Shell can attribute it via the trusted DOM event path.
          { provide: MFE_HOST_ELEMENT, useValue: this },
          {
            provide: RUNTIME_CONFIG,
            useValue: runtimeConfig,
          },
        ],
      });

      const translationService = this.appRef.injector.get(TranslationService);
      await translationService.setContext(
        this.getAttribute('locale'),
        this.getAttribute('catalogue-revision'),
      );

      // 2. Erstellt die Angular-Komponente und hängt sie direkt in dieses HTML-Tag ein
      this.componentRef = createComponent(AppRoot, {
        environmentInjector: this.appRef.injector,
        hostElement: this,
      });

      // 3. Aktiviert das Rendering im Angular-View-Tree
      this.appRef.attachView(this.componentRef.hostView);

      // 4. Liest den initialen Wert aus, den die Shell übergibt
      const initial = this.getAttribute('active-user');
      if (initial) {
        this.componentRef.setInput('activeUser', initial);
      }

      // 6. Navigiert initial zu der vom Shell vorgegebenen Route, falls vorhanden.
      const router = this.componentRef.injector.get(Router);
      await navigateToRoute(router, this.getAttribute('route'));

      // 7. Erzwingt den ersten UI-Paint
      this.componentRef.changeDetectorRef.detectChanges();
      console.log(
        '🛠️ Microfrontend infrastructure successfully registered <angular-profile-widget>',
      );
    } catch (error) {
      console.error('Error booting up Angular Web Component wrapper:', error);
    }
  }

  disconnectedCallback(): void {
    // Verhindert Memory Leaks, wenn die Shell die Ansicht wechselt
    this.componentRef?.destroy();
    this.appRef?.destroy();
  }
}

async function navigateToRoute(router: Readonly<Router>, route: null | string): Promise<void> {
  const normalizedRoute = normalizeRoute(route);
  const targetUrl = normalizedRoute ? `/${normalizedRoute}` : '/';

  const currentRoute = normalizeRoute(router.url);
  const hasSuccessfulNavigation = router.lastSuccessfulNavigation() !== null;

  /*
   * router.url may already be "/" before the first successful
   * navigation has completed.
   */
  if (currentRoute === normalizedRoute && hasSuccessfulNavigation) {
    return;
  }

  const hasNavigated = await router.navigateByUrl(targetUrl);

  if (!hasNavigated) {
    console.error(`[AngularProfileWidgetElement] Navigation to "${targetUrl}" was not completed.`);
  }
}

function normalizeRoute(route: null | string): string {
  return (route ?? '').replace(/^\/+/, '');
}

function readRuntimeConfig(element: Readonly<HTMLElement>): AngularRuntimeConfig {
  const apiBaseUrl = element.getAttribute('api-base-url');

  if (!apiBaseUrl) {
    // eslint-disable-next-line functional/no-throw-statements
    throw new Error('Angular Profile MFE requires the api-base-url attribute.');
  }

  const i18nBaseUrl = element.getAttribute('i18n-base-url');

  return Object.freeze({
    apiBaseUrl: normalizeOriginRelativeBaseUrl(apiBaseUrl, 'Angular Profile MFE api-base-url'),
    i18nBaseUrl: i18nBaseUrl
      ? normalizeOriginRelativeBaseUrl(i18nBaseUrl, 'Angular Profile MFE i18n-base-url')
      : '',
    // Required: the generated API clients cannot resolve a request without it.
    // See README, "API base URL contract" — this is a coordinated Shell change.
    apiBaseUrls: parseApiBaseUrls(
      element.getAttribute('api-base-urls'),
      'Angular Profile MFE api-base-urls',
      API_NAMES,
    ),
  });
}

// Global im Browser registrieren
if (!customElements.get('angular-profile-widget')) {
  customElements.define('angular-profile-widget', AngularProfileWidgetElement);
}

// Export, um die Datei als valides ES-Modul zu kennzeichnen
export const isBootstrapReady = true;
